import {Trans} from '@lingui/macro';
import React, {FC, useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';

import {DragAndDrop} from '../../../libs/contacts-manager/src/lib/ui/web/common/dragAndDrop/dragAndDrop';
import {DragAndDropAttachment} from '../../../libs/contacts-manager/src/lib/ui/web/common/dragAndDrop/dragAndDropAttachment';
import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../libs/shared/helpers/src/types/asyncStatuses';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {LinkTrap} from '../../components/common/linkTrap/linkTrap';
import {buildCancelTokenSource, CancelError, CancelToken} from '../../helpers/asyncCancelHelpers';
import {FileAttachmentModel, parseFileAttachment} from '../../models/api/attachmentModel';
import {HttpResponseError} from '../../services/http/httpError';
import {SettingsBannersEnum, useSettingsBanners} from '../banners/settingsBannersContext';
import {
  SettingsFooter,
  StyledSettingsContent,
  StyledSettingsMain,
  StyledSettingsSidebar,
} from '../common/settingsWrapper';
import {AllowedCustomFieldResourceImportType} from './allowedCustomFieldsResourceImportTypeHelper';
import {SettingsCustomFieldAttributeImportCsvSidebarInfoBox} from './settingsCustomFieldAttributeImportCsvSidebarInfoBox';
import {
  CsvValidationError,
  CustomFieldUploadErrorsEnum,
  DownloadTemplateLink,
  parseCsvValidationError,
} from './settingsCustomFieldAttributeImportCsvTypes';
import {SettingsCustomFieldAttributeImportError} from './settingsCustomFieldAttributeImportError';

/*
 * Types.
 */

interface AttachmentWithFile extends FileAttachmentModel {
  file: File;
}

/*
 * Props.
 */

interface CustomFieldAttributeImportCsvProps {
  requiredColumns: ReadonlyArray<string>;
  downloadTemplateCsvLink: DownloadTemplateLink;
  uploadFileAsync: (file: File, dryRun: boolean, cancelToken: CancelToken) => Promise<void>;
  resourceType: AllowedCustomFieldResourceImportType;
}

/*
 * Style.
 */

const StyledContentDiv = styled.div`
  flex: 1;
  overflow: auto;
  margin: 0 20px;
`;

const StyledAttachmentsContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
`;

const StyledErrorsContainerDiv = styled.div`
  display: flex;
  flex-flow: column;
  margin: 8px;
`;

const StyledImportButton = styled(Button)`
  flex-shrink: 1;
  margin: 16px;
`;

/*
 * Component.
 */

export const SettingsCustomFieldAttributeImportCsv: FC<CustomFieldAttributeImportCsvProps> = (props) => {
  const {downloadTemplateCsvLink, uploadFileAsync, resourceType} = props;

  const [fileAttachment, setFileAttachment] = useState<AttachmentWithFile>();
  const [errors, setErrors] = useState<ReadonlyArray<CsvValidationError>>([]);
  const [isImporting, setIsImporting] = useState(false);

  const {showBanner} = useSettingsBanners();

  const setAttachmentUploadStatus = useCallback((status: AsyncStatusesEnum) => {
    setFileAttachment(
      (prev) =>
        prev && {
          ...prev,
          uploadStatus: status,
        },
    );
  }, []);

  /*
   * Drag and drop.
   */

  const onDropFile = useCallback((file: File) => {
    if (!isValidCSVFileType(file)) {
      setFileAttachment(undefined);
      setErrors(() => [
        {error: CustomFieldUploadErrorsEnum.INVALID_FILE_TYPE, file: undefined, row: undefined},
      ]);
      return;
    }

    setFileAttachment({
      ...parseFileAttachment(file),
      file,
    });
    setErrors([]);
  }, []);

  const clearFile = useCallback(() => {
    setFileAttachment(undefined);
    setErrors([]);
  }, []);

  /*
   * Upload logic.
   */

  const testFileAsync = useCallback(
    async (file: File, cancelToken: CancelToken) => {
      try {
        await uploadFileAsync(file, true, cancelToken);
        setAttachmentUploadStatus(AsyncStatusesEnum.LOADED);
      } catch (err) {
        if (err instanceof CancelError) {
          return;
        }

        setAttachmentUploadStatus(AsyncStatusesEnum.FAILED);

        handleUploadError(err);
      }
    },
    [uploadFileAsync, setAttachmentUploadStatus],
  );

  const importAsync = useCallback(
    async (file: File, cancelToken: CancelToken) => {
      try {
        await uploadFileAsync(file, false, cancelToken);
        showBanner({
          type: SettingsBannersEnum.SUCCESS,
          message: <Trans id="settings-custom-field-attribute-import-successful">Import successful!</Trans>,
        });
        clearFile();
      } catch (err) {
        if (err instanceof CancelError) {
          return;
        }

        handleUploadError(err);
      } finally {
        setIsImporting(false);
      }
    },
    [clearFile, showBanner, uploadFileAsync],
  );

  function handleUploadError(error: HttpResponseError) {
    // The backend can return a CSV file detailing the different errors.
    if (error.file) {
      setErrors([
        {error: CustomFieldUploadErrorsEnum.ROWS_ERRORS_WITH_DOWNLOAD, file: error.file, row: undefined},
      ]);
      return;
    }

    // Otherwise, it will return a list of errors.
    const csvErrors = parseCsvValidationError(error);
    setErrors(csvErrors);
  }

  /**
   * Upload the file for dry-testing.
   */
  useEffect(() => {
    if (!fileAttachment) {
      return undefined;
    }

    if (fileAttachment.uploadStatus !== AsyncStatusesEnum.LOADING) {
      return undefined;
    }

    const cancelTokenSource = buildCancelTokenSource();

    testFileAsync(fileAttachment.file, cancelTokenSource.token);

    return () => {
      cancelTokenSource.cancel();
    };
  }, [fileAttachment, testFileAsync]);

  /**
   * Upload the file for import.
   */
  useEffect(() => {
    if (!isImporting || !fileAttachment) {
      return undefined;
    }

    const cancelTokenSource = buildCancelTokenSource();

    importAsync(fileAttachment.file, cancelTokenSource.token);

    return () => {
      cancelTokenSource.cancel();
    };
  }, [fileAttachment, importAsync, isImporting]);

  /*
   * Render.
   */

  const canImport = fileAttachment?.uploadStatus === AsyncStatusesEnum.LOADED && !isImporting;

  return (
    <>
      <StyledSettingsMain>
        <StyledSettingsContent>
          <StyledContentDiv>
            {maybeRenderDragAndDrop(fileAttachment, onDropFile)}
            {fileAttachment && (
              <>
                <StyledAttachmentsContainerDiv>
                  <DragAndDropAttachment
                    attachment={fileAttachment}
                    hasError={fileAttachment.uploadStatus === AsyncStatusesEnum.FAILED}
                    onRemoveClick={clearFile}
                    canBeRemoved
                  />
                </StyledAttachmentsContainerDiv>
                {maybeRenderUploadErrors(downloadTemplateCsvLink, props.requiredColumns, errors)}
              </>
            )}
          </StyledContentDiv>
        </StyledSettingsContent>
        <StyledSettingsSidebar>
          <SettingsCustomFieldAttributeImportCsvSidebarInfoBox
            downloadTemplateCsvLink={downloadTemplateCsvLink}
            resourceType={resourceType}
          />
        </StyledSettingsSidebar>
      </StyledSettingsMain>
      <SettingsFooter>
        <StyledImportButton
          onClick={() => setIsImporting(true)}
          variant={ButtonVariantsEnum.PRIMARY}
          isDisabled={!canImport}
        >
          <Trans id="custom-field-attribute-import-import-action">Import CSV</Trans>
        </StyledImportButton>
      </SettingsFooter>
    </>
  );
};

function maybeRenderDragAndDrop(attachment: AttachmentWithFile | undefined, onDropFile: HandlerOf<File>) {
  if (
    attachment?.uploadStatus === AsyncStatusesEnum.LOADED ||
    attachment?.uploadStatus === AsyncStatusesEnum.LOADING
  ) {
    return null;
  }

  return (
    <DragAndDrop
      shouldSupportMultipleFiles={false}
      onDropFile={onDropFile}
      hasError={Boolean(attachment?.uploadStatus === AsyncStatusesEnum.FAILED)}
    />
  );
}

function maybeRenderUploadErrors(
  downloadCsvLink: DownloadTemplateLink,
  requiredColumns: ReadonlyArray<string>,
  errors: ReadonlyArray<CsvValidationError>,
) {
  if (errors.length === 0) {
    return null;
  }

  return (
    <StyledErrorsContainerDiv>
      <LinkTrap>
        {errors.map(({error, row, file}) => (
          <SettingsCustomFieldAttributeImportError
            key={error}
            error={error}
            fileWithErrors={file}
            row={row}
            requiredColumns={requiredColumns}
            downloadCsvLink={downloadCsvLink}
          />
        ))}
      </LinkTrap>
    </StyledErrorsContainerDiv>
  );
}

/*
 * Helpers.
 */

function isValidCSVFileType(file: File) {
  return file.name.toLowerCase().endsWith('.csv');
}
